/**
 * Cyberpunk Theme - JavaScript
 * Custom animations and interactions for rustic cyberpunk aesthetic
 */

// Terminal Text Animation
class TerminalTextEffect {
  constructor(element, options = {}) {
    this.element = element;
    this.options = Object.assign(
      {
        text: element.getAttribute('data-text') || element.innerText,
        typeSpeed: parseInt(element.getAttribute('data-speed'), 10) || 50,
        blinkCursor: element.hasAttribute('data-blink-cursor') || true,
        cursorChar: element.getAttribute('data-cursor') || '_',
        startDelay: parseInt(element.getAttribute('data-delay'), 10) || 0,
        preserveLayout: true, // Always preserve layout by default
      },
      options
    );

    // Store original text and set up variables
    this.element.innerHTML = '';
    this.cursorElement = null;
    this.originalText = this.options.text;
    this.text = '';
    this.isDeleting = false;
    this.loopCount = 0;
    this.isWaiting = false;

    // Create the layout preserving container if needed
    if (this.options.preserveLayout) {
      this.createLayoutPreserver();
    }

    this.init();
  }

  createLayoutPreserver() {
    // Create an invisible copy of the full text to preserve layout
    this.layoutPreserver = document.createElement('span');
    this.layoutPreserver.textContent = this.originalText;
    this.layoutPreserver.style.visibility = 'hidden';
    this.layoutPreserver.style.position = 'absolute';
    this.layoutPreserver.style.pointerEvents = 'none';
    this.layoutPreserver.style.top = '0';
    this.layoutPreserver.style.left = '0';
    this.layoutPreserver.style.width = 'auto'; /* Auto width to measure content */
    this.layoutPreserver.style.height = '100%';
    this.layoutPreserver.style.display = 'inline-block'; /* Inline-block for proper width measurement */
    this.layoutPreserver.style.whiteSpace = 'nowrap'; /* No wrapping */
    this.layoutPreserver.style.fontSize = 'inherit'; /* Inherit font size */
    this.layoutPreserver.style.fontFamily = 'inherit'; /* Inherit font family */
    this.layoutPreserver.style.fontWeight = 'inherit'; /* Inherit font weight */
    this.layoutPreserver.style.letterSpacing = 'inherit'; /* Inherit letter spacing */

    // Add the text without extra space for more precise measurement
    this.layoutPreserver.textContent = this.originalText;

    // Force layout calculation and set container width
    this.element.appendChild(this.layoutPreserver);
    this.layoutPreserver.offsetHeight; // Force reflow

    // Get the exact width and set it on the parent container
    const exactWidth = this.layoutPreserver.offsetWidth;
    if (exactWidth > 0) {
      this.element.style.minWidth = exactWidth + 5 + 'px'; /* Add small buffer */
    }
  }

  init() {
    // Create cursor element
    this.cursorElement = document.createElement('span');
    this.cursorElement.className = 'terminal-cursor';
    this.cursorElement.innerHTML = this.options.cursorChar;

    if (this.options.blinkCursor) {
      this.cursorElement.classList.add('blink');
    }

    // Start the animation after delay
    setTimeout(() => {
      this.tick();
    }, this.options.startDelay);
  }

  tick() {
    // Calculate how much of the text has been typed
    const currentLength = this.text.length;

    // If in deleting state, remove a character
    if (this.isDeleting) {
      this.text = this.originalText.substring(0, currentLength - 1);
    } else {
      // If in typing state, add a character
      this.text = this.originalText.substring(0, currentLength + 1);
    }

    // Update the DOM - preserve exact structure to prevent shifting
    if (this.visibleTextContainer) {
      // Just update existing container content
      this.visibleTextContainer.innerHTML = this.text;
    } else {
      // First time - create the structure
      this.element.innerHTML = '';

      // Re-add the layout preserver
      if (this.options.preserveLayout) {
        this.element.appendChild(this.layoutPreserver);
      }

      // Create a container for visible text with fixed position
      this.visibleTextContainer = document.createElement('span');
      this.visibleTextContainer.className = 'terminal-text-visible';
      this.visibleTextContainer.innerHTML = this.text;

      // Add the visible text and cursor
      this.element.appendChild(this.visibleTextContainer);
      this.element.appendChild(this.cursorElement);
    }

    // Calculate typing speed (random variation for realism)
    let typeSpeed = this.options.typeSpeed;

    if (this.isDeleting) {
      typeSpeed /= 2; // Deleting is faster
    }

    // Check if complete
    if (!this.isDeleting && this.text === this.originalText) {
      // Finished typing
      typeSpeed = 5000; // Pause at end
      this.isDeleting = true;
    } else if (this.isDeleting && this.text === '') {
      // Finished deleting
      this.isDeleting = false;
      this.loopCount++;
      typeSpeed = 1000; // Pause before typing again
    }

    // Schedule the next tick
    setTimeout(() => {
      this.tick();
    }, typeSpeed);
  }
}

// Glitch Text Effect
class GlitchTextEffect {
  constructor(element, options = {}) {
    this.element = element;
    this.options = Object.assign(
      {
        text: element.getAttribute('data-text') || element.innerText,
        glitchSpeed: parseInt(element.getAttribute('data-speed'), 10) || 100,
        glitchChance: parseFloat(element.getAttribute('data-chance')) || 0.1,
        glitchDuration: parseInt(element.getAttribute('data-duration'), 10) || 1000,
      },
      options
    );

    this.originalText = this.options.text;
    this.glitching = false;

    this.init();
  }

  init() {
    this.element.innerText = this.originalText;

    // Check if this is a header title (hero-title class)
    const isHeaderTitle = this.element.classList.contains('hero-title');

    if (isHeaderTitle) {
      // Continuous glitch for header: 3 seconds ON, 2 seconds OFF
      const glitchCycle = () => {
        this.startGlitch();
        setTimeout(() => {
          this.stopGlitch();
          setTimeout(glitchCycle, 2000); // Wait 2 seconds before next glitch
        }, 3000); // Glitch for 3 seconds
      };
      glitchCycle();
    } else {
      // Hover effect to trigger glitch for non-header elements
      this.element.addEventListener('mouseenter', () => this.startGlitch());
      this.element.addEventListener('mouseleave', () => this.stopGlitch());

      // Random glitches
      setInterval(() => {
        if (Math.random() < 0.05 && !this.glitching) {
          this.startGlitch();
          setTimeout(() => this.stopGlitch(), this.options.glitchDuration);
        }
      }, 5000);
    }
  }

  startGlitch() {
    if (this.glitching) return;
    this.glitching = true;
    this.element.classList.add('glitching');
    this.glitchInterval = setInterval(() => this.glitchText(), this.options.glitchSpeed);
  }

  stopGlitch() {
    this.glitching = false;
    this.element.classList.remove('glitching');
    clearInterval(this.glitchInterval);
    this.element.innerText = this.originalText;
  }

  glitchText() {
    const textArray = this.originalText.split('');
    const textLength = textArray.length;

    // Randomly swap adjacent characters
    for (let i = 0; i < textLength - 1; i++) {
      if (Math.random() < this.options.glitchChance) {
        // Swap with next character
        const temp = textArray[i];
        textArray[i] = textArray[i + 1];
        textArray[i + 1] = temp;
      }
    }

    this.element.innerText = textArray.join('');
  }
}

// Product Carousel with Conveyor Belt Effect
class ConveyorBeltCarousel {
  constructor(element) {
    this.element = element;
    this.itemsContainer = element.querySelector('[data-conveyor-items]');
    this.items = Array.from(this.itemsContainer.children);
    this.prevButton = element.querySelector('.conveyor-belt__prev');
    this.nextButton = element.querySelector('.conveyor-belt__next');
    this.speedBars = element.querySelectorAll('.speed-bar');
    this.statusText = element.querySelector('.status-text');
    this.statusLed = element.querySelector('.status-led');

    this.currentIndex = 0;
    this.itemWidth = 0;
    this.visibleItems = 0;

    this.init();
  }

  init() {
    if (this.items.length === 0) return;

    // Duplicate items for infinite scroll effect
    this.duplicateItems();

    this.updateDimensions();
    this.setupEventListeners();
    this.createAudio();

    // Initial positioning
    this.goToSlide(0, false);
    this.updateStatus('READY');
    this.updateSpeedBars(0);
  }

  duplicateItems() {
    // Clone items for seamless infinite scroll
    const originalItems = [...this.items];

    // Add clones at the end
    originalItems.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('clone');
      this.itemsContainer.appendChild(clone);
    });

    // Add clones at the beginning
    originalItems.reverse().forEach((item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('clone');
      this.itemsContainer.insertBefore(clone, this.itemsContainer.firstChild);
    });

    // Update items array to include clones
    this.items = Array.from(this.itemsContainer.children);
    this.originalItemCount = originalItems.length;

    // Start at the first original item (after the prepended clones)
    this.currentIndex = this.originalItemCount;
  }

  setupEventListeners() {
    window.addEventListener('resize', () => {
      this.updateDimensions();
      this.goToSlide(this.currentIndex, false);
    });

    // Only add click listeners to the specific button classes
    if (this.prevButton && this.prevButton.classList.contains('conveyor-belt__prev')) {
      this.prevButton.addEventListener('click', () => this.prevSlide());
    }

    if (this.nextButton && this.nextButton.classList.contains('conveyor-belt__next')) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }
  }

  updateDimensions() {
    if (this.items.length === 0) return;

    const firstItem = this.items[0];
    const itemStyle = window.getComputedStyle(firstItem);
    const marginLeft = parseInt(itemStyle.marginLeft) || 0;
    const marginRight = parseInt(itemStyle.marginRight) || 0;

    this.itemWidth = firstItem.offsetWidth + marginLeft + marginRight;

    const trackWidth = this.element.querySelector('.conveyor-belt__track').offsetWidth;
    this.visibleItems = Math.floor(trackWidth / this.itemWidth);

    // Ensure we have at least 1 visible item
    this.visibleItems = Math.max(1, this.visibleItems);
  }

  createAudio() {
    // Create a more mechanical conveyor belt sound
    this.slideSound = new Audio();
    this.slideSound.src =
      'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
    this.slideSound.volume = 0.1;
  }

  updateStatus(status) {
    if (this.statusText) {
      this.statusText.textContent = status;
    }

    if (this.statusLed) {
      this.statusLed.style.background = status === 'READY' ? 'var(--color-neon-lime)' : 'var(--color-neon-magenta)';
    }
  }

  updateSpeedBars(activeCount) {
    this.speedBars.forEach((bar, index) => {
      if (index < activeCount) {
        bar.classList.add('active');
      } else {
        bar.classList.remove('active');
      }
    });
  }

  prevSlide() {
    this.goToSlide(this.currentIndex - 1);
    this.playSound();
    this.updateStatus('MOVING');
    this.updateSpeedBars(2);

    // Reset status after animation
    setTimeout(() => {
      this.updateStatus('READY');
      this.updateSpeedBars(0);
    }, 600);
  }

  nextSlide() {
    this.goToSlide(this.currentIndex + 1);
    this.playSound();
    this.updateStatus('MOVING');
    this.updateSpeedBars(2);

    // Reset status after animation
    setTimeout(() => {
      this.updateStatus('READY');
      this.updateSpeedBars(0);
    }, 600);
  }

  goToSlide(index, animate = true) {
    // Handle infinite scroll logic
    if (index < 0) {
      index = this.items.length - this.originalItemCount - 1;
    } else if (index >= this.items.length) {
      index = this.originalItemCount;
    }

    this.currentIndex = index;

    // Calculate transform
    const translateX = -1 * this.currentIndex * this.itemWidth;

    if (animate) {
      this.itemsContainer.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    } else {
      this.itemsContainer.style.transition = 'none';
    }

    this.itemsContainer.style.transform = `translateX(${translateX}px)`;

    // Handle infinite scroll reset
    if (animate) {
      setTimeout(() => {
        this.handleInfiniteScroll();
      }, 600);
    }

    // Update button states
    this.updateButtonStates();

    // Update visible state for animation purposes
    this.updateVisibleItems();
  }

  handleInfiniteScroll() {
    // Reset position for infinite scroll
    if (this.currentIndex <= this.originalItemCount - 1) {
      // We're at the beginning clones, jump to the end originals
      this.currentIndex = this.items.length - this.originalItemCount - 1;
      this.itemsContainer.style.transition = 'none';
      const translateX = -1 * this.currentIndex * this.itemWidth;
      this.itemsContainer.style.transform = `translateX(${translateX}px)`;
    } else if (this.currentIndex >= this.items.length - this.originalItemCount) {
      // We're at the end clones, jump to the beginning originals
      this.currentIndex = this.originalItemCount;
      this.itemsContainer.style.transition = 'none';
      const translateX = -1 * this.currentIndex * this.itemWidth;
      this.itemsContainer.style.transform = `translateX(${translateX}px)`;
    }
  }

  updateButtonStates() {
    // For infinite scroll, buttons are always enabled
    if (this.prevButton) {
      this.prevButton.disabled = false;
    }
    if (this.nextButton) {
      this.nextButton.disabled = false;
    }
  }

  updateVisibleItems() {
    this.items.forEach((item, i) => {
      const isVisible = i >= this.currentIndex && i < this.currentIndex + this.visibleItems;

      if (isVisible) {
        item.classList.add('visible');
      } else {
        item.classList.remove('visible');
      }
    });
  }

  playSound() {
    if (this.slideSound) {
      this.slideSound.currentTime = 0;
      this.slideSound.play().catch(() => {
        // Autoplay may be blocked, ignore
      });
    }
  }

  destroy() {
    // Remove event listeners
    window.removeEventListener('resize', this.updateDimensions);
  }
}

// Terminal Notification System
class TerminalNotification {
  constructor() {
    this.notificationContainer = document.createElement('div');
    this.notificationContainer.className = 'terminal-notification';
    this.notificationContainer.innerHTML = `
      <h4 class="terminal-notification__title">System Alert</h4>
      <div class="terminal-notification__content"></div>
    `;
    document.body.appendChild(this.notificationContainer);

    this.contentElement = this.notificationContainer.querySelector('.terminal-notification__content');
    this.queue = [];
    this.isShowing = false;
  }

  show(message, duration = 3000) {
    this.queue.push({ message, duration });

    if (!this.isShowing) {
      this.processQueue();
    }
  }

  processQueue() {
    if (this.queue.length === 0) {
      this.isShowing = false;
      return;
    }

    this.isShowing = true;
    const { message, duration } = this.queue.shift();

    // Set content
    this.contentElement.innerHTML = '';
    this.typeWriter(message, this.contentElement, 25);

    // Show notification
    this.notificationContainer.classList.add('show');

    // Hide after duration
    setTimeout(() => {
      this.notificationContainer.classList.remove('show');

      // Wait for transition to finish
      setTimeout(() => {
        this.processQueue();
      }, 300);
    }, duration);
  }

  typeWriter(text, element, speed = 50, index = 0) {
    if (index < text.length) {
      element.innerHTML += text.charAt(index);
      index++;
      setTimeout(() => this.typeWriter(text, element, speed, index), speed);
    }
  }
}

// Initialize components when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Init terminal text effects
  document.querySelectorAll('[data-terminal-text]').forEach((el) => {
    new TerminalTextEffect(el);
  });

  // Init glitch text effects
  document.querySelectorAll('[data-glitch-text]').forEach((el) => {
    new GlitchTextEffect(el);
  });

  // Init conveyor belt carousels
  document.querySelectorAll('[data-conveyor-belt]').forEach((el) => {
    new ConveyorBeltCarousel(el);
  });

  // Create global notification system
  window.terminalNotification = new TerminalNotification();

  // Add to cart effect
  document.querySelectorAll('.add-to-cart-button').forEach((button) => {
    button.addEventListener('click', function (e) {
      e.preventDefault();

      // Get product data
      const productCard = this.closest('.product-card');
      const productTitle = productCard ? productCard.querySelector('.product-card-title').textContent : 'Product';

      // Show notification
      window.terminalNotification.show(
        `> SYSTEM: Adding ${productTitle} to cart...\n> SYSTEM: Item successfully added.\n> CART: Updated inventory.`
      );

      // Add actual cart functionality here
    });
  });

  // Add scanline effect to all headers
  document.querySelectorAll('h1, h2, h3').forEach((header) => {
    // Create a wrapper to contain the header with position relative
    const wrapper = document.createElement('div');
    wrapper.className = 'scanline-wrapper';
    wrapper.style.position = 'relative';
    wrapper.style.overflow = 'hidden';

    // Wrap the header
    header.parentNode.insertBefore(wrapper, header);
    wrapper.appendChild(header);

    // Create the scanline element
    const scanline = document.createElement('div');
    scanline.className = 'scanline';
    scanline.style.position = 'absolute';
    scanline.style.top = '0';
    scanline.style.left = '0';
    scanline.style.width = '100%';
    scanline.style.height = '2px';
    scanline.style.backgroundColor = 'rgba(0, 255, 255, 0.3)';
    scanline.style.boxShadow = '0 0 5px rgba(0, 255, 255, 0.5)';
    scanline.style.zIndex = '1';
    scanline.style.animation = 'scanline 2s linear infinite';

    wrapper.appendChild(scanline);
  });

  // Terminal text typing effect - HTML aware version
  const typingElements = document.querySelectorAll('[data-typing-effect]');
  typingElements.forEach((element) => {
    const originalHTML = element.innerHTML;
    const delay = element.dataset.typingDelay || 30; // Default typing delay

    // Extract text content only (strip HTML tags)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = originalHTML;
    const textOnly = tempDiv.textContent || tempDiv.innerText || '';

    // Clear the content initially
    element.innerHTML = '';

    // Set up typing animation
    let i = 0;
    const typeText = () => {
      if (i < textOnly.length) {
        element.textContent += textOnly.charAt(i);
        i++;
        setTimeout(typeText, delay);
      }
    };

    // Start typing with a slight delay to ensure DOM is ready
    setTimeout(typeText, 500);
  });

  // Terminal navigation text effect
  const terminalNavItems = document.querySelectorAll('[data-terminal-nav-item]');
  terminalNavItems.forEach((item) => {
    // Store original text
    const originalText = item.textContent;
    item.setAttribute('data-original-text', originalText);

    // Measure the width of "> " prefix
    const tempSpan = document.createElement('span');
    tempSpan.textContent = '> ';
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    document.body.appendChild(tempSpan);
    const prefixWidth = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);

    // Set up the container
    item.style.position = 'relative';
    item.style.display = 'inline-block';
    item.style.overflow = 'hidden';

    // Wrap the original text content in a span with padding for the prefix
    const originalContent = document.createElement('span');
    originalContent.className = 'terminal-nav-original';
    originalContent.textContent = originalText;
    originalContent.style.display = 'inline-block';
    originalContent.style.paddingLeft = prefixWidth + 'px';

    // Clear item and add wrapped content
    item.textContent = '';
    item.appendChild(originalContent);

    // Create the prefix that slides in
    const prefix = document.createElement('span');
    prefix.className = 'terminal-nav-prefix';
    prefix.textContent = '> ';
    prefix.style.position = 'absolute';
    prefix.style.left = '0';
    prefix.style.top = '0';
    prefix.style.transform = 'translateX(-100%)';
    prefix.style.transition = 'transform 0.2s ease-out';
    item.appendChild(prefix);

    // Slide prefix in on hover
    item.addEventListener('mouseenter', function () {
      this.querySelector('.terminal-nav-prefix').style.transform = 'translateX(0)';
    });

    item.addEventListener('mouseleave', function () {
      this.querySelector('.terminal-nav-prefix').style.transform = 'translateX(-100%)';
    });
  });

  // Glitch text effect
  const glitchTextElements = document.querySelectorAll('[data-glitch-text]');
  glitchTextElements.forEach((element) => {
    element.setAttribute('data-text', element.textContent);
  });

  // Terminal button hover effect
  const terminalButtons = document.querySelectorAll('[data-terminal-button]');
  terminalButtons.forEach((button) => {
    button.addEventListener('mouseenter', function () {
      this.classList.add('active');
    });

    button.addEventListener('mouseleave', function () {
      this.classList.remove('active');
    });
  });

  // Close terminal notification
  const closeButtons = document.querySelectorAll('[data-terminal-close]');
  closeButtons.forEach((button) => {
    button.addEventListener('click', function () {
      const terminal = this.closest('.terminal-container');
      if (terminal) {
        // Add fade-out animation
        terminal.style.opacity = '0';
        terminal.style.transform = 'translateY(-20px)';
        setTimeout(() => {
          terminal.remove();
        }, 300);
      }
    });
  });

  // Scan line animation
  const scanLines = document.querySelectorAll('.scan-lines');
  scanLines.forEach((element) => {
    element.style.animation = 'scanline 2s linear infinite';
  });
});

// Add neon flicker effect to random elements occasionally
setInterval(() => {
  const neonElements = document.querySelectorAll('.neon-text, .neon-heading, .neon-link');
  if (neonElements.length > 0) {
    const randomElement = neonElements[Math.floor(Math.random() * neonElements.length)];
    randomElement.classList.add('flicker');
    setTimeout(() => {
      randomElement.classList.remove('flicker');
    }, 200);
  }
}, 5000);
